import { useState } from "react";
import { useScrollReveal } from "../../../hooks/useScrollReveal";
import { PatentCertification } from "../../../lib/supabase";

interface Props {
  patents: PatentCertification[];
  productName?: string;
}

const typeLabel: Record<string, { label: string; icon: string; color: string }> = {
  patent: { label: "Bằng Sáng Chế", icon: "ri-file-text-line", color: "#004A99" },
  certification: { label: "Chứng Nhận", icon: "ri-medal-line", color: "#059669" },
  award: { label: "Giải Thưởng", icon: "ri-trophy-line", color: "#D97706" },
};

interface LightboxProps {
  images: PatentCertification[];
  initialIndex: number;
  onClose: () => void;
}

const Lightbox = ({ images, initialIndex, onClose }: LightboxProps) => {
  const [idx, setIdx] = useState(initialIndex);
  const current = images[idx];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative max-w-4xl w-full mx-4 bg-white rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-9 h-9 flex items-center justify-center bg-black/20 hover:bg-black/40 rounded-full text-white transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-lg" />
        </button>

        {/* Image */}
        <div className="bg-gray-50 flex items-center justify-center" style={{ height: "70vh" }}>
          <img
            src={current.image_url ?? ""}
            alt={current.title}
            className="max-w-full max-h-full"
            style={{ objectFit: "contain" }}
          />
        </div>

        {/* Info */}
        <div className="p-5 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-bold text-gray-900 text-sm">{current.title}</h4>
              {current.description && (
                <p className="text-xs text-gray-500 mt-1">{current.description}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIdx((idx - 1 + images.length) % images.length)}
                disabled={images.length <= 1}
                className="w-9 h-9 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors cursor-pointer disabled:opacity-30"
              >
                <i className="ri-arrow-left-s-line" />
              </button>
              <span className="text-xs text-gray-400 font-mono">{idx + 1}/{images.length}</span>
              <button
                onClick={() => setIdx((idx + 1) % images.length)}
                disabled={images.length <= 1}
                className="w-9 h-9 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors cursor-pointer disabled:opacity-30"
              >
                <i className="ri-arrow-right-s-line" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PatentCard = ({
  item,
  index,
  onClick,
}: {
  item: PatentCertification;
  index: number;
  onClick: () => void;
}) => {
  const { ref, isVisible } = useScrollReveal(0.1);
  const meta = typeLabel[item.type] ?? typeLabel.certification;

  return (
    <div
      ref={ref}
      className="bg-white rounded-2xl overflow-hidden border border-gray-100 transition-all duration-700 cursor-pointer hover:scale-[1.02] group"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(32px)",
        transitionDelay: `${index * 80}ms`,
      }}
      onClick={onClick}
    >
      {item.image_url && (
        <div className="overflow-hidden bg-gray-50 relative" style={{ height: "180px" }}>
          <img
            src={item.image_url}
            alt={item.title}
            className="w-full h-full group-hover:scale-105 transition-transform duration-500"
            style={{ objectFit: "contain", padding: "12px" }}
          />
          {/* Zoom hint */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
            <div className="w-9 h-9 flex items-center justify-center bg-white/0 group-hover:bg-white/90 rounded-full transition-all opacity-0 group-hover:opacity-100">
              <i className="ri-zoom-in-line text-gray-700 text-sm" />
            </div>
          </div>
        </div>
      )}
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <div
            className="w-6 h-6 flex items-center justify-center rounded-lg flex-shrink-0"
            style={{ backgroundColor: `${meta.color}15` }}
          >
            <i className={`${meta.icon} text-xs`} style={{ color: meta.color }} />
          </div>
          <span
            className="text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap"
            style={{ backgroundColor: `${meta.color}12`, color: meta.color }}
          >
            {meta.label}
          </span>
        </div>
        <h4 className="text-sm font-bold text-gray-900 leading-snug mb-1">{item.title}</h4>
        {item.description && (
          <p className="text-xs text-gray-500 leading-relaxed">{item.description}</p>
        )}
      </div>
    </div>
  );
};

const ProductPatentSection = ({ patents, productName }: Props) => {
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal(0.1);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // Only show patents that have image_url
  const withImages = patents.filter((p) => p.image_url);

  if (!patents || patents.length === 0) {
    return (
      <section className="py-20 bg-[#F8FAFF]">
        <div className="max-w-[1400px] mx-auto px-8">
          <div
            ref={headerRef}
            className={`transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            <div className="text-center mb-12">
              <p className="text-xs font-bold tracking-[0.3em] text-[#004A99] uppercase mb-3">
                TIN TƯỞNG & CHỨNG NHẬN
              </p>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Cam Kết Chất Lượng
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { icon: "ri-building-4-line", title: "Công ty đã đăng ký & xác thực", desc: "Tất cả doanh nghiệp được xác minh danh tính và hoạt động hợp pháp tại Hàn Quốc." },
                { icon: "ri-file-text-line", title: "Thông tin sản phẩm minh bạch", desc: "Toàn bộ thông số kỹ thuật được dịch và kiểm tra bởi chuyên gia song ngữ." },
                { icon: "ri-translate-2", title: "Hỗ trợ biên dịch tiếng Hàn", desc: "Đội ngũ biên dịch chuyên nghiệp, hiểu sâu về kỹ thuật và thương mại." },
                { icon: "ri-customer-service-2-line", title: "Kỹ thuật viên tư vấn trực tiếp", desc: "Liên kết trực tiếp với kỹ sư phụ trách sản phẩm của nhà sản xuất." },
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#EEF4FF] rounded-xl mb-4">
                    <i className={`${item.icon} text-[#004A99] text-xl`} />
                  </div>
                  <h4 className="text-sm font-bold text-gray-900 mb-2">{item.title}</h4>
                  <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-[#F8FAFF]">
      <div className="max-w-[1400px] mx-auto px-8">
        <div
          ref={headerRef}
          className={`text-center mb-12 transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <p className="text-xs font-bold tracking-[0.3em] text-[#004A99] uppercase mb-3">
            TIN TƯỞNG & CHỨNG NHẬN
          </p>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">
            Bằng Sáng Chế & Chứng Nhận
          </h2>
          {productName && (
            <p className="text-gray-500 text-sm max-w-lg mx-auto">
              Các bằng sáng chế và chứng nhận quốc tế của{" "}
              <span className="font-semibold text-[#004A99]">{productName}</span>
            </p>
          )}
          <p className="text-xs text-gray-400 mt-2">
            <i className="ri-zoom-in-line mr-1" />
            Nhấn vào ảnh để phóng to
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {patents.map((item, i) => (
            <PatentCard
              key={i}
              item={item}
              index={i}
              onClick={() => {
                // Find index in withImages array
                const imgIdx = withImages.findIndex((p) => p.image_url === item.image_url && p.title === item.title);
                if (imgIdx >= 0) setLightboxIndex(imgIdx);
              }}
            />
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && withImages.length > 0 && (
        <Lightbox
          images={withImages}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </section>
  );
};

export default ProductPatentSection;
